import { Injectable } from '@angular/core';
import { flatMap, isEmpty, last } from 'lodash-es';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Area, IValidation, IViolation, ValidationState } from 'src/app/models';
import { CollectionState } from 'src/app/state/collection-state';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { createValidationRules, oqcValidationRule } from './validation-rules';

@Injectable({ providedIn: 'root' })
export class ValidationService {
  private readonly state = new CollectionState<IValidation>();

  public readonly validations$ = this.state.items$;

  public readonly isLoading$ = this.validations$.pipe(
    map((validations) =>
      validations.some((validation) => validation.validationState === ValidationState.LOADING)
    ),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  public readonly failingRuleCount$ = this.validations$.pipe(
    map((validations) =>
      validations
        .map((validation) => this.ruleContribution(validation))
        .reduce((prev, next) => prev + next, 0)
    ),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  public constructor(featureFlagService: FeatureFlagService) {
    this.state.set(createValidationRules());

    // isFeatureEnabled re-emits per active account, so both branches are needed:
    // appending only would duplicate the rule on re-emission and leave it behind
    // when switching to an account without the flag.
    featureFlagService.isFeatureEnabled('oqc_validation_enabled').subscribe((isEnabled) => {
      const isOqcRule = ({ id }: IValidation) => id === oqcValidationRule.id;

      if (isEnabled) {
        this.state.replaceWhere(isOqcRule, oqcValidationRule);
      } else {
        this.state.removeWhere(isOqcRule);
      }
    });
  }

  /** Replaces the whole rule set. Only the constructor and tests should need this. */
  public setRules(validations: IValidation[]) {
    this.state.set(validations);
  }

  public selectValidationById(validationId: string): Observable<IValidation | undefined> {
    return this.state.select(({ items }) => items.find(({ id }) => id === validationId));
  }

  public selectValidationStateById(validationId: string) {
    return this.selectValidationById(validationId).pipe(
      map((validation) => validation?.validationState)
    );
  }

  public selectErrorById(validationId: string) {
    return this.selectValidationById(validationId).pipe(map((validation) => validation?.error));
  }

  public selectHasViolationsById(validationId: string) {
    return this.selectValidationById(validationId).pipe(
      map((validation) => !isEmpty(validation?.violations))
    );
  }

  // TODO this is not feature complete
  public selectHasViolationsInPath(area: Area) {
    return this.validations$.pipe(
      map((validations) =>
        validations.some(
          (validation) =>
            !isEmpty(validation.violations) &&
            validation.violations.some((violation) =>
              violation.pathToViolation.some((path) => path.area === area)
            )
        )
      )
    );
  }

  // TODO this is not feature complete
  public selectViolationsByLastPath(area: Area): Observable<IViolation[]> {
    return this.validations$.pipe(
      map((validations) =>
        flatMap(
          validations.filter((validation) => !isEmpty(validation.violations)),
          (validation) =>
            validation.violations.filter((violation) => {
              const lastPath = last(violation.pathToViolation);

              return lastPath ? lastPath.area === area : false;
            })
        )
      )
    );
  }

  public updateValidation(validationId: string | string[], validation: Partial<IValidation>) {
    this.updateByIds(validationId, validation);
  }

  /**
   * Sets Violations for an existing Validation rule
   *
   * @remarks
   * It's expected to be called once with all violations
   *
   * @param validationId ValidationRule to add violations to
   * @param violations Violations to be added, if empty array it will be handled as the validation passed
   */
  public setViolations(validationId: string, violations: IViolation[]) {
    const validationState = violations.length > 0 ? ValidationState.WARNING : ValidationState.VALID;

    this.updateByIds(validationId, { validationState, violations });
  }

  public setErrorOnValidation(
    validationIds: string | string[],
    error: string,
    diagnostics?: IValidation['diagnostics']
  ) {
    this.updateByIds(validationIds, {
      diagnostics,
      error,
      validationState: ValidationState.FAILED,
    });
  }

  public setValidationsToLoading(validationIds: string | string[]) {
    this.updateByIds(validationIds, {
      diagnostics: undefined,
      error: undefined,
      validationState: ValidationState.LOADING,
      violations: [],
    });
  }

  public reset() {
    this.resetValidations(this.state.items.map(({ id }) => id));
  }

  public resetValidations(validationIds: string | string[]) {
    this.updateByIds(validationIds, {
      diagnostics: undefined,
      error: undefined,
      validationState: ValidationState.INITIAL,
      violations: [],
    });
  }

  // Each rule adds at most 1 to the button counter. A single rule can produce
  // many violations at once (e.g. an empty or partially completed OQC file),
  // which would otherwise flood the count with noise; individual violations are
  // still listed in the modal.
  private ruleContribution(validation: IValidation): number {
    if (validation.validationState === ValidationState.FAILED) {
      return 1;
    }

    return validation.violations.length > 0 ? 1 : 0;
  }

  private updateByIds(idOrIds: string | string[], partial: Partial<IValidation>) {
    const ids = new Set(Array.isArray(idOrIds) ? idOrIds : [idOrIds]);

    this.state.updateWhere(({ id }) => ids.has(id), partial);
  }
}
